/**
 * The profile page's words (src/profile, docs/UI.md "Profile"): what the card says about you and your motor,
 * built from plain data so it is unit-tested (profileStats.test.ts) and the same offline and online.
 *
 *  - Offline it reads the save: the stats, the story jobs done, the races, the half-bricks, the contact
 *    ladders' titles, the miles driven.
 *  - Online it reads the server's record (`characterRecord`, shared/messages/economy.ts) and the store's gang.
 *
 * Notoriety is a ladder of six (0..5 stars) derived from honest counts — never a hidden score the game plays
 * by, only a name for what the record already says. Nothing here touches game state.
 */
import { TRACKS, type ContactTrack } from '../../shared/contacts';
import { CAMBERS, GARAGE_PAINTS, NEON_COLOURS, RIM_COLOURS, RIMS, TRACKS as TRACK_WIDTHS, type CarMods } from '../../shared/garage';
import type { CharacterRecordView } from '../../shared/messages/economy';
import { PAINTS } from '../activities/garage';
import type { ActivitiesSave } from '../activities/state';
import type { SaveStats } from '../save/saveData';
import { MAYHEM_CHALLENGES } from '../activities/mayhem';

export interface ProfileRow {
  label: string;
  value: string;
}

export interface Notoriety {
  /** 0..5 */
  stars: number;
  name: string;
  /** Points earned and the points the next star wants (null at the top). */
  points: number;
  next: number | null;
}

/** The ladder, lowest first: points needed for each star and its name. */
export const NOTORIETY: readonly { at: number; name: string }[] = [
  { at: 0, name: 'Nobody yet' },
  { at: 20, name: 'A face' },
  { at: 60, name: 'A name on the manor' },
  { at: 140, name: 'Known to the law' },
  { at: 280, name: 'A proper villain' },
  { at: 500, name: 'Legend of Low Borough' },
];

export function notorietyFor(points: number): Notoriety {
  const p = Math.max(0, Math.floor(points));
  let stars = 0;
  for (let i = 1; i < NOTORIETY.length; i++) if (p >= NOTORIETY[i]!.at) stars = i;
  return { stars, name: NOTORIETY[stars]!.name, points: p, next: stars + 1 < NOTORIETY.length ? NOTORIETY[stars + 1]!.at : null };
}

export const starsText = (n: number): string => `${'★'.repeat(n)}${'☆'.repeat(Math.max(0, 5 - n))}`;

const pounds = (n: number): string => `£${Math.round(n).toLocaleString('en-GB')}`;

/** "3 h 12 min", "25 min", "under a minute". */
export function timeText(seconds: number): string {
  const m = Math.floor(Math.max(0, seconds) / 60);
  if (m < 1) return 'under a minute';
  const h = Math.floor(m / 60);
  return h ? `${h} h ${m % 60} min` : `${m} min`;
}

/** Metres driven as the borough would say it: yards under a tenth of a mile, miles after. */
export function distanceText(metres: number): string {
  const m = Math.max(0, metres);
  const miles = m / 1609.344;
  if (miles < 0.1) return `${Math.round(m * 1.0936)} yards`;
  if (miles < 10) return `${miles.toFixed(1)} miles`;
  return `${Math.round(miles).toLocaleString('en-GB')} miles`;
}

// ------------------------------------------------------------------------------------------------ offline

export interface OfflineProfileInput {
  money: number;
  stats: SaveStats;
  completedMissions: readonly string[];
  /** Story job ids (for "x of y"). */
  story: readonly string[];
  activities: ActivitiesSave;
  /** Weapons owned besides the fists. */
  weapons: number;
  /** Rung per contact track (0 = stranger). */
  rungs: Partial<Record<ContactTrack, number>>;
}

/** The highest title any contact has given you ("One of Mags’ — Mags Delaney"), or null. */
export function bestTitle(rungs: Partial<Record<ContactTrack, number>>): { title: string; contact: string; rung: number } | null {
  let best: { title: string; contact: string; rung: number } | null = null;
  for (const [track, def] of Object.entries(TRACKS) as [ContactTrack, (typeof TRACKS)[ContactTrack]][]) {
    const rung = Math.min(def.rungs.length, Math.max(0, Math.floor(rungs[track] ?? 0)));
    for (let r = rung; r >= 1; r--) {
      const d = def.rungs[r - 1]!;
      if (!d.title) continue;
      if (!best || r > best.rung) best = { title: d.name, contact: def.name, rung: r };
      break;
    }
  }
  return best;
}

export function offlineNotoriety(i: OfflineProfileInput): Notoriety {
  const st = i.stats;
  const a = i.activities;
  const storyDone = i.story.filter((id) => i.completedMissions.includes(id)).length;
  const stamped = MAYHEM_CHALLENGES.filter((c) => (a.mayhem.tier[c.id] ?? 0) > 0).length;
  const rungSum = Object.values(i.rungs).reduce((n, r) => n + (r ?? 0), 0);
  const pts =
    storyDone * 40 +
    st.exportsDone * 4 +
    st.streetJobsDone * 3 +
    st.carsStolen * 2 +
    st.bestEscape * 20 +
    a.race.wins * 6 +
    a.cab.runs * 2 +
    stamped * 6 +
    a.matchbooks.length * 2 +
    rungSum * 4 +
    Math.min(150, Math.floor(st.moneyEarned / 200));
  return notorietyFor(pts);
}

export function offlineRows(i: OfflineProfileInput): ProfileRow[] {
  const st = i.stats;
  const a = i.activities;
  const storyDone = i.story.filter((id) => i.completedMissions.includes(id)).length;
  const rows: ProfileRow[] = [
    { label: 'Cash', value: pounds(i.money) },
    { label: 'On the manor', value: timeText(st.playTime) },
    { label: 'Jobs done', value: `${storyDone} of ${i.story.length} · ${st.exportsDone + st.streetJobsDone} odd jobs` },
    { label: 'Motors nicked', value: st.carsStolen.toLocaleString('en-GB') },
    { label: 'Miles driven', value: distanceText(st.distanceDriven) },
    { label: 'Best getaway', value: st.bestEscape ? `${'★'.repeat(st.bestEscape)} shaken off` : 'none yet' },
    { label: 'Nicked · wasted', value: `${st.timesBusted} · ${st.timesWasted}` },
    { label: 'Races won', value: a.race.runs ? `${a.race.wins} of ${a.race.runs}` : 'none run' },
    { label: 'Earned all told', value: pounds(st.moneyEarned) },
  ];
  if (i.weapons > 0) rows.push({ label: 'Tools of the trade', value: `${i.weapons}` });
  return rows;
}

// ------------------------------------------------------------------------------------------------- online

export interface OnlineProfileInput {
  cash: number;
  rec: CharacterRecordView | null;
  gang: { name: string; tag: string; role: string; tier: number } | null;
  rungs: Partial<Record<ContactTrack, number>>;
}

export function onlineNotoriety(i: OnlineProfileInput): Notoriety {
  const r = i.rec;
  if (!r) return notorietyFor(0);
  const rungSum = Object.values(i.rungs).reduce((n, x) => n + (x ?? 0), 0);
  const pts =
    r.jobs * 6 +
    r.carsStolen * 2 +
    r.bestEscape * 20 +
    r.crewRuns * 10 +
    r.crates +
    r.eventsWon * 15 +
    r.districtsLed * 12 +
    rungSum * 4 +
    Math.min(150, Math.floor(r.earned / 200));
  return notorietyFor(pts);
}

export function onlineRows(i: OnlineProfileInput): ProfileRow[] {
  const r = i.rec;
  const rows: ProfileRow[] = [{ label: 'Cash', value: pounds(i.cash) }];
  if (!r) {
    rows.push({ label: 'The record', value: 'asking around…' });
    return rows;
  }
  rows.push(
    { label: 'In the city', value: timeText(r.playTimeS) },
    { label: 'Jobs paid', value: r.jobs.toLocaleString('en-GB') },
    { label: 'Motors nicked', value: r.carsStolen.toLocaleString('en-GB') },
    { label: 'Best getaway', value: r.bestEscape ? `${'★'.repeat(r.bestEscape)} shaken off` : 'none yet' },
    { label: 'Times nicked', value: r.arrests.toLocaleString('en-GB') },
    { label: 'Night Freight', value: r.crewRuns ? `${r.crewRuns} runs · ${r.crates} crates` : 'no runs yet' },
    { label: 'Earned all told', value: pounds(r.earned) },
  );
  if (i.gang) rows.push({ label: 'Firm', value: `${i.gang.name} [${i.gang.tag}] · ${i.gang.role}` });
  if (r.districtsLed) rows.push({ label: 'Districts run', value: String(r.districtsLed) });
  return rows;
}

// ---------------------------------------------------------------------------------------------- the motor

export interface CarCaptionInput {
  name: string;
  paint: number;
  mods: CarMods;
  /** Where it is kept ("Lockup 7, Pilcher Ginnel"), or null. */
  kept: string | null;
  /** Online, a stored car: the server sends no mods for it, so what shows is the paint alone. */
  modsUnknown?: boolean;
}

export interface CarCaption {
  /** "Vesper · Pillar-box red, flat matt" */
  title: string;
  /** The mods, each a short phrase (empty when stock). */
  mods: string[];
  kept: string | null;
  swatch: number;
}

const paintName = (hex: number): string | null => GARAGE_PAINTS.find((p) => p.hex === hex)?.name ?? PAINTS.find((p) => p.hex === hex)?.name ?? null;

export function carCaption(c: CarCaptionInput): CarCaption {
  const m = c.mods;
  const paint = paintName(c.paint);
  const finish = m.finish === 'matt' ? 'flat matt' : m.finish === 'metallic' ? 'metallic flake' : m.finish === 'gloss' ? 'solid gloss' : null;
  const title = [c.name, [paint, finish].filter(Boolean).join(', ')].filter(Boolean).join(' · ');
  const mods: string[] = [];
  if (m.wheels !== null) {
    const rim = RIMS.find((r) => r.id === m.wheels)?.name ?? m.wheels;
    const col = m.rim !== null ? RIM_COLOURS.find((s) => s.hex === m.rim)?.name.toLowerCase() : null;
    mods.push(col ? `${rim} in ${col}` : rim);
  } else if (m.rim !== null) mods.push(`${RIM_COLOURS.find((s) => s.hex === m.rim)?.name.toLowerCase() ?? 'painted'} rims`);
  if (m.ride === -1) mods.push('lowered');
  else if (m.ride === 1) mods.push('raised');
  if (m.camber > 0) mods.push(`${CAMBERS.find((x) => x.id === m.camber)?.deg ?? m.camber}° camber`);
  if (m.track !== 0) mods.push(`${(TRACK_WIDTHS.find((t) => t.id === m.track)?.name ?? 'wide').toLowerCase()} track`);
  if (m.neon !== null) mods.push(`${NEON_COLOURS.find((s) => s.hex === m.neon)?.name.toLowerCase() ?? 'neon'} underglow`);
  if (m.livery !== null) mods.push('gang livery');
  if (c.modsUnknown) mods.push('as stored — Pike’s work shows once it’s out');
  return { title, mods, kept: c.kept, swatch: c.paint };
}
